/* Billing Terms → Manage surcharge types: add a type with your own name, rename it, stop it for every client from a
   pickup date, or restart it. Types are kept in Reason Master (PTL_SURCHARGE_TYPE); a stopped type is not charged on
   shipments picked up from that date, whatever the client's terms say. Needs PTL_BILLING; every change is audited. */
import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { useAddSurchargeTypeMutation, useRenameSurchargeTypeMutation, useStopSurchargeTypeMutation,
  useSurchargeTypeListQuery } from '../../../redux/ptlBillingApiSlice'
import { errText, fmtDate } from '../billingUtils'

export default function SurchargeTypesDialog({ onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const { data: types = [], isFetching, error } = useSurchargeTypeListQuery()
  const [add, { isLoading: adding }] = useAddSurchargeTypeMutation()
  const [rename, { isLoading: renaming }] = useRenameSurchargeTypeMutation()
  const [stop, { isLoading: stopping }] = useStopSurchargeTypeMutation()
  const [newName, setNewName] = useState('')
  const [editing, setEditing] = useState(null) // { code, label }
  const [stopping_, setStopping] = useState(null) // { code, label, from }

  const done = (res, ok) => {
    if (res.error) { enqueueSnackbar(errText(res.error), { variant: 'error' }); return false }
    enqueueSnackbar(ok, { variant: 'success' })
    return true
  }
  const doAdd = async () => {
    if (newName.trim().length < 2) return enqueueSnackbar('Type a surcharge name (at least 2 characters).', { variant: 'error' })
    if (done(await add(newName.trim()), `${newName.trim()} added`)) setNewName('')
  }
  const doRename = async () => {
    if (done(await rename({ code: editing.code, label: editing.label.trim() }), 'Renamed')) setEditing(null)
  }
  const doStop = async () => {
    if (!stopping_.from) return enqueueSnackbar('Pick the first pickup date without this surcharge.', { variant: 'error' })
    if (done(await stop({ code: stopping_.code, from: stopping_.from }), `${stopping_.label} stops from ${fmtDate(stopping_.from)}`)) setStopping(null)
  }
  const doRestart = async (t) => done(await stop({ code: t.code, from: null }), `${t.label} charged again`)

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Manage surcharge types</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          A type is the name of a surcharge (e.g. War surcharge, CYS charge). Each client's rate and how it is charged are set in its terms.
          Stopping a type stops it for every client for shipments picked up from that date; earlier shipments keep it. Every change is audited.
        </Typography>
        {error && <Alert severity='error'>{errText(error)}</Alert>}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField size='small' label='New surcharge name' placeholder='e.g. CYS charge' value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doAdd() }} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 60 }} sx={{ flex: 1, minWidth: 200 }} />
          <LoadingButton variant='contained' loading={adding} onClick={doAdd} sx={{ textTransform: 'none' }}>Add type</LoadingButton>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow><TableCell>Name</TableCell><TableCell>Code</TableCell><TableCell>Status</TableCell><TableCell align='right'>Actions</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {types.length === 0 && !isFetching && <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary' }}>No surcharge types yet.</TableCell></TableRow>}
              {types.map((t) => (
                <TableRow key={t.code}>
                  <TableCell sx={{ minWidth: 160 }}>
                    {editing?.code === t.code
                      ? <TextField size='small' fullWidth autoFocus value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') doRename() }} inputProps={{ maxLength: 60 }} />
                      : t.label}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{t.code}</TableCell>
                  <TableCell>
                    {t.stopFrom ? <Chip size='small' color='warning' label={`Stops from ${fmtDate(t.stopFrom)}`} /> : <Chip size='small' color='success' label='Charged' />}
                  </TableCell>
                  <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                    {editing?.code === t.code ? (
                      <>
                        <LoadingButton size='small' loading={renaming} onClick={doRename} sx={{ textTransform: 'none' }}>Save</LoadingButton>
                        <Button size='small' onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button size='small' onClick={() => setEditing({ code: t.code, label: t.label })} sx={{ textTransform: 'none' }}>Rename</Button>
                        {t.stopFrom
                          ? <LoadingButton size='small' color='success' loading={stopping} onClick={() => doRestart(t)} sx={{ textTransform: 'none' }}>Restart</LoadingButton>
                          : <Button size='small' color='warning' onClick={() => setStopping({ code: t.code, label: t.label, from: new Date().toISOString().slice(0, 10) })} sx={{ textTransform: 'none' }}>Stop</Button>}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {stopping_ && (
          <Alert severity='warning'>
            Stop <b>{stopping_.label}</b> for every client: shipments picked up on or after this date are not charged it.
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
              <TextField size='small' type='date' label='Stop from (pickup date)' value={stopping_.from} onChange={(e) => setStopping({ ...stopping_, from: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <LoadingButton size='small' variant='contained' color='warning' loading={stopping} onClick={doStop} sx={{ textTransform: 'none' }}>Stop for all</LoadingButton>
              <Button size='small' onClick={() => setStopping(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  )
}
